import { axiosCustom } from 'common/common.function';
import {
  DetailJobExcelInput,
  JobDetailSearchExcelInput,
  GetNoticeListInput,
  GetNoticeListOutput,
  GetNoticeOutput,
  CreateNoticeInput,
  CreateNoticeOutput,
  UpdateNoticeInput,
  UpdateNoticeOutput,
  DeleteNoticeOutput,
  ToggleNoticeVisibilityOutput,
  GetCoupangInquiryLogOutput,
} from 'components/form/interfaces/admin.interface';
import { ValidateOAuthResultInput, ValidateOAuthResultOutput } from 'components/form/interfaces/member.interface';

/**
 * @Post
 * @url ${process.env.REACT_APP_BACKEND_ENDPOINT}/api/admin/detail-search/excel-download
 * @returns - 200 { success: true, result: Blob }
 * @returns - 403 { success: false, message: '다운로드 실패' }
 * @description - 어드민 상세 조회 엑셀 다운로드 함수
 */
export async function jobDetailSearchExcelMutation({
  startDate,
  endDate,
  solType,
  searchType,
  searchValue,
  downloadReason,
  password,
  isIntegrated,
}: JobDetailSearchExcelInput): Promise<Blob> {
  const jwt = localStorage.getItem('jwt');
  const response = await axiosCustom.post(
    `${process.env.REACT_APP_BACKEND_ENDPOINT}/api/admin/detail-search/excel-download`,
    {},
    {
      headers: {
        jwt: jwt,
        'sol-type': isIntegrated ? 'ADMIN' : solType.toUpperCase(),
      },
      params: {
        startDate,
        endDate: endDate + ' 23:59:59',
        solType,
        searchType,
        searchValue,
        downloadReason,
        password,
      },
      responseType: 'blob',
    },
  );
  return response;
}

/**
 * @Post
 * @url ${process.env.REACT_APP_BACKEND_ENDPOINT}/api/admin/detail-job/excel-download
 * @returns - 200 { success: true, result: Blob }
 * @returns - 403 { success: false, message: '다운로드 실패' }
 * @description 어드민 - 상세 조회 - 작업상세결과 다운로드 함수
 */
export async function detailJobExcelMutation({
  startDate,
  endDate,
  solType,
  searchType,
  searchValue,
  downloadReason,
  password,
  isIntegrated,
}: DetailJobExcelInput): Promise<Blob> {
  const jwt = localStorage.getItem('jwt');
  const response = await axiosCustom.post(
    `${process.env.REACT_APP_BACKEND_ENDPOINT}/api/admin/detail-job/excel-download`,
    {},
    {
      headers: {
        jwt: jwt,
        'sol-type': isIntegrated ? 'ADMIN' : solType.toUpperCase(),
      },
      params: {
        startDate,
        endDate: endDate + ' 23:59:59',
        solType,
        searchType,
        searchValue,
        downloadReason,
        password,
      },
      responseType: 'blob',
    },
  );
  return response;
}

/**
 * @alias OAuth 결과 검증
 */
export const validateOAuthResult = async (body: ValidateOAuthResultInput): Promise<ValidateOAuthResultOutput> => {
  const response = await axiosCustom.post(`${process.env.REACT_APP_BACKEND_ENDPOINT}/api/job/st11/validate-oauth-result`, body);

  console.log('response', response);

  return response;
};

/**
 * @alias 쿠팡 문의용 request/response 로그 단건 조회 (상품 1개)
 * @url GET ${process.env.REACT_APP_BACKEND_ENDPOINT}/api/admin/detail-job/:jobId/coupang-inquiry-logs/:tempProdCode
 * @description 쿠팡 상품 등록 실패 문의 시 메일 첨부용 request/response 조회. 없으면 404.
 */
export async function getCoupangInquiryLog(
  jobId: number,
  tempProdCode: string,
  solType: string,
  isIntegrated?: boolean,
): Promise<GetCoupangInquiryLogOutput> {
  const jwt = localStorage.getItem('jwt');
  const response = await axiosCustom.get(
    `${process.env.REACT_APP_BACKEND_ENDPOINT}/api/admin/detail-job/${jobId}/coupang-inquiry-logs/${encodeURIComponent(tempProdCode)}`,
    {
      headers: {
        jwt: jwt,
        'sol-type': isIntegrated ? 'ADMIN' : solType.toUpperCase(),
      },
    },
  );
  return response;
}

// ---------------------------------------
// 공지사항
// ---------------------------------------

/**
 * @alias 공지사항 목록 조회
 */
export async function getNoticeList(params: GetNoticeListInput): Promise<GetNoticeListOutput> {
  const jwt = localStorage.getItem('jwt');
  const response = await axiosCustom.get(`${process.env.REACT_APP_PAWN_ADMIN_BACKEND_ENDPOINT}/api/notice/list`, {
    params,
    headers: {
      jwt: jwt,
    },
  });
  return response;
}

/**
 * @alias 공지사항 단건 조회
 */
export async function getNotice(noticeId: number): Promise<GetNoticeOutput> {
  const jwt = localStorage.getItem('jwt');
  const response = await axiosCustom.get(`${process.env.REACT_APP_PAWN_ADMIN_BACKEND_ENDPOINT}/api/notice/${noticeId}`, {
    headers: {
      jwt: jwt,
    },
  });
  return response;
}

/**
 * @alias 공지사항 생성
 */
export async function createNotice(body: CreateNoticeInput): Promise<CreateNoticeOutput> {
  const jwt = localStorage.getItem('jwt');
  const response = await axiosCustom.post(`${process.env.REACT_APP_PAWN_ADMIN_BACKEND_ENDPOINT}/api/notice/create`, body, {
    headers: {
      jwt: jwt,
    },
  });
  return response;
}

/**
 * @alias 공지사항 수정
 */
export async function updateNotice(body: UpdateNoticeInput): Promise<UpdateNoticeOutput> {
  const jwt = localStorage.getItem('jwt');
  const { noticeId, ...restBody } = body;
  const response = await axiosCustom.patch(`${process.env.REACT_APP_PAWN_ADMIN_BACKEND_ENDPOINT}/api/notice/update/${noticeId}`, restBody, {
    headers: {
      jwt: jwt,
    },
  });
  return response;
}

/**
 * @alias 공지사항 삭제
 */
export async function deleteNotice(noticeId: number): Promise<DeleteNoticeOutput> {
  const jwt = localStorage.getItem('jwt');
  const response = await axiosCustom.delete(`${process.env.REACT_APP_PAWN_ADMIN_BACKEND_ENDPOINT}/api/notice/delete/${noticeId}`, {
    headers: {
      jwt: jwt,
    },
  });
  return response;
}

/**
 * @alias 공지사항 노출 여부 변경
 */
export async function toggleNoticeVisibility(noticeId: number): Promise<ToggleNoticeVisibilityOutput> {
  const jwt = localStorage.getItem('jwt');
  const response = await axiosCustom.patch(`${process.env.REACT_APP_PAWN_ADMIN_BACKEND_ENDPOINT}/api/notice/toggle-visible/${noticeId}`, {
    headers: {
      jwt: jwt,
    },
  });
  return response;
}
